// UserDetailPage.tsx — manage a single user: edit profile, assign roles, delete.
import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import NameTag from '../components/NameTag'
import Combobox from 'common/ui/Combobox'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import { BsPlusLg, BsSave, BsTrash } from 'react-icons/bs'
import Text from 'common/ui/Text'
import { useConfirm } from 'common/ui/ConfirmDialog'
import { useAuth, apiFetch } from '../hooks/useAuth'

interface UserData {
    email: string
    full_name?: string
    provider?: string
    email_confirmed?: boolean
    created_at?: string
    last_login_at?: string
    photo_url?: string
}

interface RoleData {
    name: string
    description?: string
    permissions: string[]
}

const avatarStyle: React.CSSProperties = {
    width: 72,
    height: 72,
    borderRadius: '50%',
    border: '1px solid var(--bs-border-color)',
    flexShrink: 0,
}

// UserAvatar shows the social-login photo, falling back to the email initial
// when there is none or it fails to load.
const UserAvatar = ({ email, photoUrl }: { email: string; photoUrl?: string }) => {
    const [failed, setFailed] = useState(false)
    useEffect(() => setFailed(false), [photoUrl])
    if (photoUrl && !failed) {
        return (
            <img
                src={photoUrl}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setFailed(true)}
                style={{ ...avatarStyle, objectFit: 'cover' }}
            />
        )
    }
    return (
        <div
            aria-hidden="true"
            className="d-flex align-items-center justify-content-center text-muted"
            style={{
                ...avatarStyle,
                background: 'var(--bs-secondary-bg)',
                fontSize: '1.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
            }}
        >
            {email ? email.charAt(0) : '?'}
        </div>
    )
}

const UserDetailPage = () => {
    const { id = '' } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { email: actorEmail, loading, error: authError } = useAuth()
    const [user, setUser] = useState<UserData | null>(null)
    const [allRoles, setAllRoles] = useState<RoleData[]>([])
    const [userRoles, setUserRoles] = useState<RoleData[]>([])
    const [permissions, setPermissions] = useState<string[]>([])
    const [fullName, setFullName] = useState('')
    const [emailField, setEmailField] = useState('')
    const [addRole, setAddRole] = useState('')
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [fetchErr, setFetchErr] = useState('')

    const fetchAll = useCallback(async () => {
        setFetchErr('')
        try {
            const [userRes, rolesRes, userRolesRes, permsRes] = await Promise.all([
                apiFetch(`/api/admin/users/${encodeURIComponent(id)}`),
                apiFetch('/api/admin/roles'),
                apiFetch(`/api/admin/users/${encodeURIComponent(id)}/roles`),
                apiFetch(`/api/admin/users/${encodeURIComponent(id)}/permissions`),
            ])
            if (!userRes.ok) {
                setFetchErr(userRes.status === 404 ? `User "${id}" not found` : 'Failed to load user')
                return
            }
            const userData: UserData = await userRes.json()
            setUser(userData)
            setFullName(userData.full_name || '')
            setEmailField(userData.email || id)

            if (rolesRes.ok) {
                const data = await rolesRes.json()
                setAllRoles(data.roles || [])
            }
            if (userRolesRes.ok) {
                const data = await userRolesRes.json()
                setUserRoles(data.roles || [])
            }
            if (permsRes.ok) {
                const data = await permsRes.json()
                setPermissions(data.permissions || [])
            }
        } catch {
            setFetchErr('Failed to load user')
        }
    }, [id])

    useEffect(() => {
        if (!loading && actorEmail) fetchAll()
    }, [loading, actorEmail, fetchAll])

    const assignRoles = async (roleNames: string[]) => {
        const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}/roles`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ roles: roleNames }),
        })
        if (!res.ok) {
            const data = await res.json().catch(() => ({}))
            throw new Error(data.error || 'Failed to update roles')
        }
    }

    const handleAddRole = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        const target = addRole.trim()
        if (!target) return
        if (userRoles.some((r) => r.name === target)) {
            setError(`Already has role ${target}`)
            return
        }
        if (!allRoles.some((r) => r.name === target)) {
            setError(`Role "${target}" does not exist`)
            return
        }
        try {
            const next = [...userRoles.map((r) => r.name), target]
            await assignRoles(next)
            setSuccess(`Added ${target}`)
            setAddRole('')
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to add role')
        }
    }

    const handleRemoveRole = async (roleName: string) => {
        setError(''); setSuccess('')
        try {
            const next = userRoles.map((r) => r.name).filter((n) => n !== roleName)
            await assignRoles(next)
            setSuccess(`Removed ${roleName}`)
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to remove role')
        }
    }

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault()
        setError(''); setSuccess('')
        if (!user) return
        const body: Record<string, string> = {}
        if (fullName !== (user.full_name || '')) body.full_name = fullName
        if (emailField !== user.email) body.email = emailField
        if (Object.keys(body).length === 0) return
        try {
            const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || 'Failed to save profile')
            }
            setSuccess('Profile updated')
            // If email was renamed the URL is now stale; route to the new one.
            if (body.email && body.email !== id) {
                navigate(`/users/${encodeURIComponent(body.email)}`, { replace: true })
                return
            }
            fetchAll()
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save profile')
        }
    }

    const [confirm, confirmDialog] = useConfirm()

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Delete User',
            body: <>Delete user <strong>{id}</strong>? This cannot be undone.</>,
            confirmLabel: 'Delete',
        })
        if (!ok) return
        setError(''); setSuccess('')
        try {
            const res = await apiFetch(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' })
            if (!res.ok) {
                const data = await res.json().catch(() => ({}))
                throw new Error(data.error || 'Delete failed')
            }
            navigate('/users', { replace: true })
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Delete failed')
        }
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center p-5">
                <Spinner animation="border" />
            </div>
        )
    }

    if (fetchErr) {
        return (
            <>
                <h1 className="mb-4">User</h1>
                <Alert variant="danger">{fetchErr}</Alert>
                <Link to="/users">← Back to users</Link>
            </>
        )
    }

    if (!user) return null

    const remainingRoles = allRoles.filter((r) => !userRoles.some((ur) => ur.name === r.name))
    const isSelf = actorEmail === user.email
    const sortedPerms = [...permissions].sort()

    return (
        <>
            <Stack direction="horizontal" className="mb-4 align-items-center">
                <h1 className="m-0 d-flex align-items-center gap-2">
                    <Link to="/users" className="text-muted text-decoration-none">Users</Link>
                    <span className="text-muted">/</span>
                    <code>{user.email}</code>
                </h1>
                <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-auto d-flex align-items-center gap-1"
                    onClick={handleDelete}
                    disabled={isSelf}
                    title={isSelf ? 'Cannot delete yourself' : undefined}
                >
                    <BsTrash /> Delete user
                </Button>
            </Stack>

            {authError && <Alert variant="danger">{authError}</Alert>}
            {error && <Alert variant="danger">{error}</Alert>}
            {success && <Alert variant="success">{success}</Alert>}

            <Card className="mb-3">
                <Card.Header>Account</Card.Header>
                <Card.Body className="d-flex align-items-center gap-3">
                    <UserAvatar email={user.email} photoUrl={user.photo_url} />
                    <Table size="sm" borderless className="config-table m-0" style={{ width: 'auto' }}>
                        <tbody>
                            <tr>
                                <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Provider</td>
                                <td className="fw-bold">{user.provider || 'local'}</td>
                            </tr>
                            <tr>
                                <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Email confirmed</td>
                                <td className="fw-bold">{user.email_confirmed ? 'yes' : 'no'}</td>
                            </tr>
                            {user.created_at && (
                                <tr>
                                    <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Created</td>
                                    <td className="fw-bold"><code>{user.created_at}</code></td>
                                </tr>
                            )}
                            {user.last_login_at && (
                                <tr>
                                    <td className="text-muted text-end pe-3 align-middle" style={{ whiteSpace: 'nowrap' }}>Last login</td>
                                    <td className="fw-bold"><code>{user.last_login_at}</code></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Card className="mb-3">
                <Card.Header>Profile</Card.Header>
                <Card.Body>
                    <Form id="profile-form" onSubmit={handleSaveProfile}>
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Text
                                type="email"
                                style={{ width: '100%' }}
                                value={emailField}
                                onChange={(e) => setEmailField(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Full name</Form.Label>
                            <Text
                                type="text"
                                style={{ width: '100%' }}
                                placeholder="optional"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button
                        variant="primary"
                        type="submit"
                        form="profile-form"
                        disabled={fullName === (user.full_name || '') && emailField === user.email}
                        className="d-flex align-items-center gap-2"
                    >
                        <BsSave /> Save
                    </Button>
                </Card.Footer>
            </Card>

            <Card className="mb-3">
                <Card.Header>Roles ({userRoles.length})</Card.Header>
                <Card.Body>
                    {userRoles.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2 mb-3">
                            {userRoles.map((r) => (
                                <NameTag
                                    key={r.name}
                                    name={r.name}
                                    kind="role"
                                    to={`/roles/${encodeURIComponent(r.name)}`}
                                    actions={[{ icon: <BsTrash />, onClick: () => handleRemoveRole(r.name), label: `Remove ${r.name}`, tone: 'danger' }]}
                                />
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted small">No roles assigned.</p>
                    )}
                    <Form id="add-user-role-form" onSubmit={handleAddRole}>
                        <Form.Group>
                            <Form.Label>Add role</Form.Label>
                            <Combobox
                                value={addRole}
                                onChange={setAddRole}
                                options={remainingRoles.map((r) => r.name)}
                                style={{ width: '100%' }}
                                placeholder="role.name (autocompletes from existing roles)"
                            />
                        </Form.Group>
                    </Form>
                </Card.Body>
                <Card.Footer>
                    <Button
                        variant="success"
                        type="submit"
                        form="add-user-role-form"
                        disabled={!addRole.trim()}
                        className="d-flex align-items-center gap-2"
                    >
                        <BsPlusLg /> Add
                    </Button>
                </Card.Footer>
            </Card>

            <Card className="mb-3">
                <Card.Header>Effective scopes ({sortedPerms.length})</Card.Header>
                <Card.Body>
                    {sortedPerms.length > 0 ? (
                        <div className="d-flex flex-wrap column-gap-3 row-gap-1">
                            {sortedPerms.map((p) => (
                                <NameTag key={p} name={p} kind="scope" to={`/scopes/${encodeURIComponent(p)}`} />
                            ))}
                        </div>
                    ) : (
                        <span className="text-muted small">No scopes granted</span>
                    )}
                </Card.Body>
            </Card>

            {confirmDialog}
        </>
    )
}

export default UserDetailPage
